/**
 * Endpoints de **Finanzas → Caja Chica** (`/api/finanzas/caja-chica`).
 *
 * El resumen trae el saldo disponible, los contadores del periodo, los catálogos
 * (bancos, cuentas, métodos de pago y categorías) y los permisos de la sesión;
 * el historial va paginado en el servidor con los totales de todo el filtro. Las
 * cinco operaciones (saldo inicial, ingreso, egreso, reajuste y depósito) se
 * envían como `multipart/form-data` —las que llevan adjunto— o como JSON.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CuerpoDepositoCaja,
  CuerpoMovimientoCaja,
  CuerpoReajusteCaja,
  FiltrosCaja,
  HistorialCaja,
  ResumenCaja,
} from './caja-chica.types';

export const cajaChicaKeys = {
  todo: ['finanzas', 'caja-chica'] as const,
  resumen: (desde: string, hasta: string) =>
    ['finanzas', 'caja-chica', 'resumen', desde, hasta] as const,
  lista: (filtros: FiltrosCaja) => ['finanzas', 'caja-chica', 'lista', filtros] as const,
};

/** Cadena de consulta del listado y de las exportaciones. */
export function cadenaDeCaja(filtros: FiltrosCaja): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.tipo) parametros.set('tipo', filtros.tipo);
  if (filtros.categoria) parametros.set('categoria', filtros.categoria);
  if (filtros.buscar) parametros.set('buscar', filtros.buscar);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

/** Arma el formulario de un movimiento con su adjunto (si lo hay). */
function formularioDe(
  datos: Record<string, string>,
  archivo: File | null,
): FormData {
  const formulario = new FormData();

  Object.entries(datos).forEach(([clave, valor]) => formulario.set(clave, valor));

  if (archivo) formulario.set('archivo', archivo);

  return formulario;
}

export const cajaChicaApi = {
  resumen: (desde: string, hasta: string, signal?: AbortSignal): Promise<ResumenCaja> =>
    apiClient.get<ResumenCaja>(
      `/api/finanzas/caja-chica?desde=${desde}&hasta=${hasta}`,
      { signal },
    ),

  historial: (filtros: FiltrosCaja, signal?: AbortSignal): Promise<HistorialCaja> =>
    apiClient.get<HistorialCaja>(
      `/api/finanzas/caja-chica/movimientos${cadenaDeCaja(filtros)}`,
      { signal },
    ),

  ingreso: (datos: CuerpoMovimientoCaja) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/caja-chica/ingreso', {
      body: formularioDe(
        {
          fecha: datos.fecha,
          monto: datos.monto,
          categoria: datos.categoria,
          concepto: datos.concepto,
          referencia: datos.referencia,
          clave_idempotencia: datos.clave_idempotencia ?? '',
        },
        datos.archivo,
      ),
    }),

  egreso: (datos: CuerpoMovimientoCaja) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/caja-chica/egreso', {
      body: formularioDe(
        {
          fecha: datos.fecha,
          monto: datos.monto,
          categoria: datos.categoria,
          concepto: datos.concepto,
          referencia: datos.referencia,
          clave_idempotencia: datos.clave_idempotencia ?? '',
        },
        datos.archivo,
      ),
    }),

  apertura: (datos: { fecha: string; monto: string; concepto: string; clave_idempotencia?: string }) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/caja-chica/apertura', {
      body: datos,
    }),

  ajuste: (datos: CuerpoReajusteCaja) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/caja-chica/ajuste', {
      body: datos,
    }),

  deposito: (datos: CuerpoDepositoCaja) =>
    apiClient.post<{ egreso_id: number; movement_id: number; message: string }>(
      '/api/finanzas/caja-chica/deposito',
      {
        body: formularioDe(
          {
            fecha: datos.fecha,
            monto: datos.monto,
            cuenta_id: datos.cuenta_id,
            concepto: datos.concepto,
            referencia: datos.referencia,
            clave_idempotencia: datos.clave_idempotencia ?? '',
          },
          datos.archivo,
        ),
      },
    ),

  revertir: (id: number) =>
    apiClient.delete<{ reversion_id: number; movement_id_anulado: number | null; message: string }>(
      `/api/finanzas/caja-chica/movimientos/${id}`,
    ),

  urlExportar: (filtros: FiltrosCaja, formato: 'csv' | 'pdf') =>
    `/api/finanzas/caja-chica/exportar${cadenaDeCaja(filtros)}${
      cadenaDeCaja(filtros) ? '&' : '?'
    }formato=${formato}`,
};

/** Resumen del módulo (saldo, contadores, catálogos y permisos). */
export function useResumenCaja(desde: string, hasta: string) {
  return useQuery({
    queryKey: cajaChicaKeys.resumen(desde, hasta),
    queryFn: ({ signal }) => cajaChicaApi.resumen(desde, hasta, signal),
  });
}

/** Historial paginado. */
export function useHistorialCaja(filtros: FiltrosCaja) {
  return useQuery({
    queryKey: cajaChicaKeys.lista(filtros),
    queryFn: ({ signal }) => cajaChicaApi.historial(filtros, signal),
  });
}

/** Invalida el resumen y el historial tras cualquier operación. */
function useRefrescar() {
  const cliente = useQueryClient();

  return () => cliente.invalidateQueries({ queryKey: cajaChicaKeys.todo });
}

export function useRegistrarIngresoCaja() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (datos: CuerpoMovimientoCaja) => cajaChicaApi.ingreso(datos),
    onSuccess: refrescar,
  });
}

export function useRegistrarEgresoCaja() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (datos: CuerpoMovimientoCaja) => cajaChicaApi.egreso(datos),
    onSuccess: refrescar,
  });
}

export function useAperturarCaja() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (datos: { fecha: string; monto: string; concepto: string; clave_idempotencia?: string }) =>
      cajaChicaApi.apertura(datos),
    onSuccess: refrescar,
  });
}

export function useAjustarCaja() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (datos: CuerpoReajusteCaja) => cajaChicaApi.ajuste(datos),
    onSuccess: refrescar,
  });
}

export function useDepositarCaja() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (datos: CuerpoDepositoCaja) => cajaChicaApi.deposito(datos),
    onSuccess: refrescar,
  });
}

export function useRevertirMovimientoCaja() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (id: number) => cajaChicaApi.revertir(id),
    onSuccess: refrescar,
  });
}
